import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Bell,
  Briefcase,
  MessageCircle,
  Search,
  User,
} from 'lucide-react';

const posterFiles = [
  '/posters/Screenshot 2026-03-04 110342.png',
  '/posters/Screenshot 2026-03-04 110347.png',
  '/posters/Screenshot 2026-03-04 110353.png',
  '/posters/Screenshot 2026-03-04 110401.png',
  '/posters/Screenshot 2026-03-04 110406.png',
  '/posters/Screenshot 2026-03-04 110411.png',
  '/posters/Screenshot 2026-03-04 110418.png',
  '/posters/Screenshot 2026-03-04 110429.png',
];

const wallPositions = [
  { left: 0.07, top: 0.12, rotate: -7, scale: 0.88, pinColor: '#ef4444' },
  { left: 0.22, top: 0.07, rotate: 4, scale: 0.84, pinColor: '#3b82f6' },
  { left: 0.37, top: 0.1, rotate: -3, scale: 0.9, pinColor: '#f59e0b' },
  { left: 0.56, top: 0.06, rotate: 6, scale: 0.83, pinColor: '#ef4444' },
  { left: 0.76, top: 0.11, rotate: -4, scale: 0.87, pinColor: '#22c55e' },
  { left: 0.11, top: 0.49, rotate: 5, scale: 0.86, pinColor: '#3b82f6' },
  { left: 0.43, top: 0.53, rotate: -6, scale: 0.83, pinColor: '#f59e0b' },
  { left: 0.73, top: 0.5, rotate: 3, scale: 0.88, pinColor: '#ef4444' },
];

const allJobs = [
  { title: 'Industrial Welder', company: 'Al Rashed Group', country: 'SA', salary: '2,800 SAR', tag: 'Urgent', color: '#ef4444' },
  { title: 'Civil Foreman', company: 'Gulf Stars LLC', country: 'AE', salary: '3,200 AED', tag: 'New', color: '#22c55e' },
  { title: 'Electrician', company: 'Qatar Energy Co.', country: 'QA', salary: '4,000 QAR', tag: 'Hot', color: '#f59e0b' },
  { title: 'Scaffolder', company: 'Sky Manpower', country: 'DE', salary: 'EUR 2,100', tag: 'New', color: '#3b82f6' },
  { title: 'Factory Electrician', company: 'Tata Projects', country: 'IN', salary: 'INR 28,000', tag: 'New', color: '#f97316' },
  { title: 'Construction Mason', company: 'L&T Heavy Eng.', country: 'IN', salary: 'INR 22,000', tag: '450+ openings', color: '#8b5cf6' },
  { title: 'Pipe Fitter', company: 'HPCL Refinery', country: 'IN', salary: 'INR 32,000', tag: 'Urgent', color: '#ef4444' },
];

type PosterStyle = {
  left: string;
  top: string;
  transform: string;
  opacity: number;
  zIndex: number;
};

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));
const lerp = (from: number, to: number, progress: number) => from + (to - from) * progress;

const easeOutCubic = (value: number) => 1 - Math.pow(1 - clamp01(value), 3);

const easeInOutCubic = (value: number) => {
  const clamped = clamp01(value);
  if (clamped < 0.5) {
    return 4 * clamped * clamped * clamped;
  }
  return 1 - Math.pow(-2 * clamped + 2, 3) / 2;
};

const getProgress = (
  frame: number,
  start: number,
  duration: number,
  easing: (value: number) => number = easeInOutCubic
) => easing(clamp01((frame - start) / Math.max(duration, 1)));

const getTimeline = (fps: number) => {
  const wallHold = Math.round(0.75 * fps);
  const flyDuration = Math.round(0.65 * fps);
  const flyStagger = Math.max(1, Math.round(0.07 * fps));
  const phoneIntroStart = wallHold + (posterFiles.length - 1) * flyStagger + Math.round(0.15 * fps);
  const phoneIntroDuration = Math.round(0.55 * fps);
  const reelStart = phoneIntroStart + Math.round(0.25 * fps);
  const reelPosterFrames = Math.round(0.9 * fps);
  const reelEnd = reelStart + reelPosterFrames * 5;
  const feedIntroDuration = Math.round(0.5 * fps);
  const feedScrollDuration = Math.round(2.6 * fps);
  const totalFrames = reelEnd + feedIntroDuration + feedScrollDuration;

  return {
    wallHold,
    flyDuration,
    flyStagger,
    phoneIntroStart,
    phoneIntroDuration,
    reelStart,
    reelPosterFrames,
    reelEnd,
    feedIntroDuration,
    feedScrollDuration,
    totalFrames,
  };
};

const FeedCard: React.FC<{
  title: string;
  company: string;
  country: string;
  salary: string;
  tag: string;
  color: string;
}> = ({ title, company, country, salary, tag, color }) => (
  <div
    className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.06] p-2.5"
    style={{ minHeight: 72 }}
  >
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-white/8 text-[11px] font-black text-white/85">
      {country}
    </div>
    <div className="min-w-0 flex-1">
      <p className="truncate text-[11px] font-black leading-tight text-white">{title}</p>
      <p className="truncate pt-0.5 text-[8px] text-white/45">{company}</p>
    </div>
    <div className="shrink-0 text-right">
      <p className="text-[9px] font-black text-white/80">{salary}</p>
      <span
        className="mt-1 inline-block rounded-full px-1.5 py-[2px] text-[7px] font-bold"
        style={{ backgroundColor: `${color}25`, color }}
      >
        {tag}
      </span>
    </div>
  </div>
);

const RecruiterPosterPhoneExport: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    document.documentElement.classList.add('jobisa-export-mode');
    document.body.classList.add('jobisa-export-mode');

    return () => {
      document.documentElement.classList.remove('jobisa-export-mode');
      document.body.classList.remove('jobisa-export-mode');
    };
  }, []);

  const { frame, fps } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const requestedFps = Number.parseInt(params.get('fps') || '30', 10);
    const safeFps = Number.isFinite(requestedFps) && requestedFps > 0 ? requestedFps : 30;
    const timeline = getTimeline(safeFps);
    const requestedFrame = Number.parseInt(params.get('frame') || '0', 10);
    const safeFrame = Number.isFinite(requestedFrame)
      ? Math.max(0, Math.min(requestedFrame, timeline.totalFrames - 1))
      : 0;

    return { frame: safeFrame, fps: safeFps };
  }, [location.search]);

  const timeline = useMemo(() => getTimeline(fps), [fps]);
  const phoneCenterX = 0.67;
  const phoneCenterY = 0.55;
  const phoneIntro = getProgress(frame, timeline.phoneIntroStart, timeline.phoneIntroDuration, easeOutCubic);
  const phoneScale = lerp(0.72, 1, phoneIntro);
  const phoneOpacity = phoneIntro;
  const phoneTranslateY = lerp(70, 0, phoneIntro);
  const phoneRotate = lerp(6, 0, phoneIntro);

  const reelFrame = Math.max(0, frame - timeline.reelStart);
  const reelPosterFrames = Math.max(1, timeline.reelPosterFrames);
  const reelIndex = Math.min(4, Math.floor(reelFrame / reelPosterFrames));
  const reelInnerProgress = clamp01((reelFrame % reelPosterFrames) / reelPosterFrames);
  const inFeedPhase = frame >= timeline.reelEnd;
  const feedIntro = getProgress(frame, timeline.reelEnd, timeline.feedIntroDuration);
  const feedScroll = getProgress(frame, timeline.reelEnd + Math.round(0.1 * fps), timeline.feedScrollDuration, easeOutCubic);
  const feedTranslateY = lerp(0, -160, feedScroll);

  const posterStyles: PosterStyle[] = wallPositions.map((position, index) => {
    const progress = getProgress(
      frame,
      timeline.wallHold + index * timeline.flyStagger,
      timeline.flyDuration,
      easeInOutCubic
    );
    const left = lerp(position.left, phoneCenterX, progress);
    const top = lerp(position.top, phoneCenterY, progress);
    const rotate = lerp(position.rotate, 0, progress);
    const scale = lerp(position.scale, 0.18, progress);
    const opacity = lerp(1, 0, progress);
    const lift = lerp(0, -18, progress);

    return {
      left: `${left * 100}%`,
      top: `${top * 100}%`,
      transform: `translate(-50%, -50%) translateY(${lift}px) rotate(${rotate}deg) scale(${scale})`,
      opacity,
      zIndex: 10 + posterFiles.length - index,
    };
  });

  return (
    <div
      className="relative h-screen w-screen overflow-hidden"
      style={{ background: 'transparent' }}
    >
      <div className="absolute inset-0" style={{ background: 'transparent' }} />

      {posterFiles.map((src, index) => (
        <div
          key={src}
          className="absolute pointer-events-none"
          style={posterStyles[index]}
        >
          <div className="absolute left-1/2 top-[-14px] z-10 -translate-x-1/2">
            <div
              className="h-4 w-4 rounded-full border-2 border-white/35 shadow-md"
              style={{ backgroundColor: wallPositions[index].pinColor }}
            />
            <div className="mx-auto -mt-1 h-2 w-1 rounded-b-full bg-neutral-400" />
          </div>

          <div className="relative w-[180px] overflow-hidden rounded-xl bg-[#fffdf7] shadow-[0_10px_34px_rgba(0,0,0,0.22)]">
            <img
              src={src}
              alt={`Poster ${index + 1}`}
              className="block h-auto w-full"
              loading="eager"
            />
          </div>

          <div className="absolute bottom-[-7px] left-1/2 h-2 w-12 -translate-x-1/2 rounded-sm bg-yellow-200/75" />
        </div>
      ))}

      <div
        className="absolute pointer-events-none"
        style={{
          left: `${phoneCenterX * 100}%`,
          top: `${phoneCenterY * 100}%`,
          transform: `translate(-50%, -50%) translateY(${phoneTranslateY}px) rotate(${phoneRotate}deg) scale(${phoneScale})`,
          opacity: phoneOpacity,
          zIndex: 50,
        }}
      >
        <div className="relative h-[700px] w-[342px]">
          <div className="absolute inset-0 rounded-[3.2rem] bg-slate-200 p-[1.5px] shadow-[0_30px_80px_-10px_rgba(0,0,0,0.32)]">
            <div className="relative h-full w-full overflow-hidden rounded-[3rem] border-[8px] border-slate-950 bg-slate-900 shadow-inner">
              <div className="absolute inset-0 bg-[#0f1117]">
                <div className="flex items-center justify-between px-4 pb-1 pt-3">
                  <span className="text-[10px] font-bold text-white">9:41</span>
                  <div className="flex items-center gap-1.5">
                    <div className="flex h-2.5 items-end gap-[2px]">
                      {[5, 7, 9, 9].map((height, index) => (
                        <div
                          key={index}
                          className="w-[2px] rounded-full bg-white/60"
                          style={{ height }}
                        />
                      ))}
                    </div>
                    <div className="relative h-2 w-4 rounded-[2px] border border-white/50">
                      <div className="absolute bottom-[1px] left-[1px] right-[3px] top-[1px] rounded-[1px] bg-white/60" />
                      <div className="absolute right-[-2px] top-[2px] h-[4px] w-[2px] rounded-r-full bg-white/40" />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between px-4 pb-3 pt-1">
                  <div>
                    <p className="text-[9px] font-medium text-white/50">Namaste</p>
                    <p className="text-[15px] font-black tracking-tight text-white">
                      jobisa<span className="text-blue-500">.</span>
                    </p>
                  </div>
                  <div className="flex gap-1.5">
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white/10">
                      <Bell size={11} className="text-white/70" />
                    </div>
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500">
                      <User size={11} className="text-white" />
                    </div>
                  </div>
                </div>

                {!inFeedPhase ? (
                  <div className="flex h-[calc(100%-96px)] flex-col overflow-hidden px-3 pb-2">
                    <div className="mb-2 flex gap-1">
                      {Array.from({ length: 5 }).map((_, index) => {
                        const fill =
                          index < reelIndex
                            ? 1
                            : index === reelIndex
                              ? reelInnerProgress
                              : 0;

                        return (
                          <div key={index} className="h-[2px] flex-1 overflow-hidden rounded-full bg-white/20">
                            <div
                              className="h-full rounded-full bg-white"
                              style={{ width: `${fill * 100}%` }}
                            />
                          </div>
                        );
                      })}
                    </div>

                    <div className="relative flex-1 overflow-hidden rounded-[1.4rem] bg-black">
                      <img
                        src={posterFiles[reelIndex]}
                        alt={`Poster preview ${reelIndex + 1}`}
                        className="absolute inset-0 h-full w-full object-contain"
                        loading="eager"
                        style={{
                          opacity: 1,
                          transform: `scale(${lerp(1.03, 1, reelInnerProgress)})`,
                        }}
                      />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-3">
                        <p className="text-[8px] font-bold uppercase tracking-[0.22em] text-white/50">
                          Recruitment Poster
                        </p>
                        <p className="pt-0.5 text-[10px] font-semibold text-white">
                          Tap to apply. Posted by verified agency.
                        </p>
                      </div>
                    </div>

                    <p className="pt-2 text-center text-[9px] font-medium text-white/30">
                      Swipe to see all posters
                    </p>
                  </div>
                ) : (
                  <div
                    className="flex h-[calc(100%-96px)] flex-col overflow-hidden"
                    style={{
                      opacity: feedIntro,
                      transform: `translateY(${lerp(26, 0, feedIntro)}px)`,
                    }}
                  >
                    <div className="px-3 pb-2">
                      <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/8 px-3 py-2">
                        <Search size={10} className="text-white/40" />
                        <span className="text-[10px] text-white/30">
                          Welder, Mason, Electrician...
                        </span>
                      </div>
                    </div>

                    <div className="flex-1 overflow-hidden px-3 pb-2">
                      <div
                        className="space-y-2"
                        style={{ transform: `translateY(${feedTranslateY}px)` }}
                      >
                        <div className="flex items-center justify-between pb-1 pt-1">
                          <p className="text-[9px] font-black uppercase tracking-[0.18em] text-white/60">
                            International
                          </p>
                          <p className="text-[9px] font-semibold text-blue-500">See all</p>
                        </div>

                        {allJobs.slice(0, 4).map((job) => (
                          <FeedCard key={job.title} {...job} />
                        ))}

                        <div className="flex items-center justify-between pb-1 pt-2">
                          <p className="text-[9px] font-black uppercase tracking-[0.18em] text-white/60">
                            India Jobs
                          </p>
                          <p className="text-[9px] font-semibold text-blue-500">See all</p>
                        </div>

                        {allJobs.slice(4).map((job) => (
                          <FeedCard key={job.title} {...job} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className="absolute inset-x-0 bottom-0 flex items-center justify-around border-t border-white/[0.08] px-3 py-2">
                  {[
                    { icon: <Briefcase size={13} />, label: 'Jobs', active: true },
                    { icon: <MessageCircle size={13} />, label: 'Chat', active: false },
                    { icon: <User size={13} />, label: 'Profile', active: false },
                  ].map((tab) => (
                    <div
                      key={tab.label}
                      className={`flex flex-col items-center gap-0.5 ${
                        tab.active ? 'text-blue-500' : 'text-white/25'
                      }`}
                    >
                      {tab.icon}
                      <span className="text-[7px] font-bold">{tab.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="absolute left-1/2 top-4 z-20 h-[26px] w-[92px] -translate-x-1/2 rounded-full bg-black" />
          <div className="absolute left-[-2px] top-28 h-11 w-[2.5px] rounded-l bg-slate-300 shadow-sm" />
          <div className="absolute left-[-2px] top-40 h-8 w-[2.5px] rounded-l bg-slate-300 shadow-sm" />
          <div className="pointer-events-none absolute inset-[8px] z-10 rounded-[2.4rem] bg-gradient-to-tr from-transparent via-white/[0.02] to-white/5" />
        </div>
      </div>
    </div>
  );
};

export default RecruiterPosterPhoneExport;
